/**
 * Máquina de estados da manchete (§13.2): estado cru pela tabela de §13.2.2, histerese (§13.2.3, H1–H8), chave de
 * transição (H7) e o modelo de texto de cada estado. Pura. Liga-se ao `selectView` por `ctx.headlinePolicy =
 * nextHeadline` (§14.8.9) e ao computed `headline` de state/derived.ts por `headlineAt` (DE-B02, G-10).
 *
 * Desvio de assinatura (registrado): `model/` não importa `copy/` (§2.2.2), então `headlineText` devolve as CHAVES do
 * catálogo e as variáveis já formatadas (§13.4); quem monta o texto rico com `tRich` é o componente `Headline` (§7.4).
 * `HeadlineState` mora em model/types.ts (ResultsView o usa) e é reexportado aqui (decisão da Fase 2).
 */
import { fmtInt, fmtSections, fmtVotesLong } from '../format/numbers';
import { fmtCountdown, fmtTime } from '../format/time';
import { ufWithData } from './nightPhase';
import { currentElectionDate, epochMs, T_DAY, T_OPEN, tMinOfIso } from './time';
import type { Candidate, CandidateNumber, ElectionEvent, HeadlineState, ResultsView } from './types';

export type { HeadlineState };

/** §13.2.1 */
export interface HeadlineInputs {
  /** View exibida (ao vivo ou replay). */
  view: ResultsView | null;
  /** Relógio da fonte em t_min de Brasília. */
  clockTMin: number;
  /** config.features.probability */
  probabilityOn: boolean;
  /** Resultado de displayProbability() (§14.6.4). */
  probabilityVisible: boolean;
}

/** §13.2.1 */
export interface HeadlineMemory {
  state: HeadlineState;
  /** Líder no momento do último cálculo. */
  leaderNumber: CandidateNumber | null;
  /** Recuo aguardando confirmação (H2). */
  pending: { state: HeadlineState; seqs: number[] } | null;
  /** seq da view usada no último cálculo. */
  seq: number | null;
}

/** Ordem de "nível" de §13.2.3, usada só para detectar recuo. */
export const HEADLINE_LEVEL: Readonly<Record<HeadlineState, number>> = Object.freeze({
  connecting: 0,
  countdown: 1,
  opened_no_votes: 2,
  abroad_first: 3,
  early: 4,
  leading: 5,
  lead_change: 5,
  above_99: 6,
  decided: 7,
  elected_tse: 8,
});

/** Janela de `lead_change` em minutos de apuração (H5). */
export const LEAD_CHANGE_WINDOW_MIN = 20;

// ── Derivações de §13.2.1 ──────────────────────────────────────────────────────────────────────────────────────
type LeadChange = ElectionEvent & { data: { type: 'national_lead_change'; to: CandidateNumber } };

/** `lastLeadChange`: o `national_lead_change` mais recente com `tMin(occurred_at) ≤ view.instantTMin`. */
export function lastLeadChange(view: ResultsView): LeadChange | null {
  let best: LeadChange | null = null;
  let bestT = Number.NEGATIVE_INFINITY;
  for (const e of view.events) {
    if (e.type !== 'national_lead_change' || e.data.type !== 'national_lead_change') continue;
    const t = tMinOfIso(e.occurred_at);
    if (!(t <= view.instantTMin) || t < bestT) continue;
    best = e as LeadChange;
    bestT = t;
  }
  return best;
}

/** Número do líder da contagem (`X`), ou null em empate / sem votos. */
function leaderOf(view: ResultsView): CandidateNumber | null {
  const i = view.national.leader;
  return i === null ? null : (view.candidates[i]?.number ?? null);
}

// ── §13.2.2 ────────────────────────────────────────────────────────────────────────────────────────────────────
/** Pura. Estado "cru" pela tabela de §13.2.2 (vale a primeira condição verdadeira), sem histerese. */
export function rawHeadline(inp: HeadlineInputs): { state: HeadlineState; leaderNumber: CandidateNumber | null } {
  const v = inp.view;
  if (v === null) return { state: 'connecting', leaderNumber: null };
  if (inp.clockTMin < T_OPEN) return { state: 'countdown', leaderNumber: null };
  const n = v.national;
  if (n.tseResult !== null) return { state: 'elected_tse', leaderNumber: n.tseResult.winner };
  if (n.mathDecision !== null) return { state: 'decided', leaderNumber: n.mathDecision.winner };
  const anyUf = ufWithData(v);
  const anyData = anyUf || (v.places.ZZ?.sectionsFraction ?? 0) > 0 || n.sectionsFraction > 0;
  if (!anyData) return { state: 'opened_no_votes', leaderNumber: null };
  if (n.sectionsFraction < 0.01 && !anyUf) return { state: 'abroad_first', leaderNumber: null };
  if (n.sectionsFraction < 0.01) return { state: 'early', leaderNumber: null };
  const x = leaderOf(v);
  const call = v.probability?.call;
  // Linha 8 + H2a: o kill switch "status ≠ active" faz a condição deixar de valer (AC-13.2-06), além da visibilidade.
  if (
    x !== null &&
    inp.probabilityOn &&
    inp.probabilityVisible &&
    v.probability?.status === 'active' &&
    call?.state === 'above_99' &&
    call.approved_at &&
    call.candidate === x
  ) {
    return { state: 'above_99', leaderNumber: x };
  }
  if (x !== null) {
    const lc = lastLeadChange(v);
    if (lc && lc.data.to === x && v.instantTMin - tMinOfIso(lc.occurred_at) < LEAD_CHANGE_WINDOW_MIN) {
      return { state: 'lead_change', leaderNumber: x };
    }
  }
  return { state: 'leading', leaderNumber: x };
}

// ── §13.2.3 ────────────────────────────────────────────────────────────────────────────────────────────────────
/** Pura. 'live' aplica a histerese (H1, H2, H2a); 'replay' ignora a memória (H3). H2b (época) é do FeedClient. */
export function nextHeadline(mem: HeadlineMemory | null, inp: HeadlineInputs, mode: 'live' | 'replay'): HeadlineMemory {
  const raw = rawHeadline(inp);
  const seq = inp.view?.seq ?? null;
  const fresh: HeadlineMemory = { state: raw.state, leaderNumber: raw.leaderNumber, pending: null, seq };
  if (mode === 'replay' || mem === null) return fresh;
  // H2a: saída de above_99 imediata (kill switch); H1: avanço (ou mesmo nível) no mesmo render.
  if (mem.state === 'above_99' && raw.state !== 'above_99') return fresh;
  if (HEADLINE_LEVEL[raw.state] >= HEADLINE_LEVEL[mem.state]) return fresh;
  // H2: recuo só com o mesmo estado cru em 2 snapshots aceitos seguidos, de seqs diferentes.
  if (seq === null) return mem;
  const held = mem.state === 'leading' || mem.state === 'lead_change';
  const leaderNumber = held && inp.view ? (leaderOf(inp.view) ?? mem.leaderNumber) : mem.leaderNumber;
  const p = mem.pending;
  if (p !== null && p.state === raw.state) {
    if (p.seqs.includes(seq)) return { ...mem, leaderNumber, seq };
    return fresh;
  }
  return { state: mem.state, leaderNumber, pending: { state: raw.state, seqs: [seq] }, seq };
}

/**
 * Estado exibido agora (FE §9.2 `headline`, G-10): `view.headline`, salvo os estados que só dependem do relógio
 * (contagem regressiva e abertura das 17h00), recalculados no tique de `now` (H8: às 17h00:00, no próprio tique).
 */
export function headlineAt(view: ResultsView | null, clockTMin: number, probabilityOn: boolean): HeadlineState {
  if (view === null) return 'connecting';
  const s = view.headline;
  if (!isClockState(s)) return s;
  return rawHeadline({ view, clockTMin, probabilityOn, probabilityVisible: view.probability !== null }).state;
}

/** Estados da manchete que dependem só do relógio (G-10): quem os exibe assina o tique de `now`. */
export function isClockState(s: HeadlineState): boolean {
  return s === 'countdown' || s === 'opened_no_votes';
}

/**
 * Estados cujo corpo do herói é o `PreResultsGrid` (§7.2 bloco 4: `connecting`, `countdown`, `opened_no_votes`).
 * Antes das 17h00 o herói nunca mostra percentual nem votos, qualquer que seja o payload (E01, AC-7.17-03).
 */
export function isPreHeadline(s: HeadlineState): boolean {
  return s === 'connecting' || isClockState(s);
}

// ── Contagem regressiva (§10.8) ────────────────────────────────────────────────────────────────────────────────
export type CountdownVariant = 'days' | 'tomorrow' | 'today' | 'simulation';

/**
 * Variante do `Countdown` (§10.8): `simulation` com `config.mode = 'simulation'` (em qualquer data); senão pela data
 * de Brasília do relógio da fonte: antes de 24/10 → `days`; 24/10 → `tomorrow`; 25/10 antes das 17h00 → `today`.
 */
export function countdownVariant(clockTMin: number, simulation: boolean): CountdownVariant {
  if (simulation) return 'simulation';
  if (clockTMin < -T_DAY) return 'days';
  if (clockTMin < 0) return 'tomorrow';
  return 'today';
}

// ── Texto (§13.2.2, §13.2.4) e chave de transição (H7) ─────────────────────────────────────────────────────────
/** Chaves do catálogo (src/copy/strings.ts) que a manchete usa; o tipo é um subconjunto de `CopyKey`. */
export type HeadlineCopyKey =
  | 'headline.connecting'
  | 'headline.opened'
  | 'headline.abroadFirst'
  | 'headline.early'
  | 'headline.leading'
  | 'headline.leading.by'
  | 'headline.tie'
  | 'headline.leadChange'
  | 'headline.above99'
  | 'headline.elected'
  | 'headline.decided.lead'
  | 'headline.decided.lead.sub'
  | 'headline.elected.tse'
  | 'countdown.days'
  | 'countdown.tomorrow'
  | 'countdown.today'
  | 'countdown.simulation'
  | 'countdown.timer';

/** Variáveis de `t()`/`tRich()`: `{X}` + `X.number` (trecho de candidato) + `X.gender` (concordância, §13.4.5). */
export type HeadlineVars = Readonly<Record<string, string | null>>;

export interface HeadlineLine {
  key: HeadlineCopyKey;
  vars: HeadlineVars;
  /** Número animado da linha (§7.4: `{V}` de `leading`), com o valor cru; o texto final já está em `vars`. */
  animated?: { name: string; value: number };
}

export interface HeadlineText {
  line1: HeadlineLine;
  line2: HeadlineLine | null;
  /** Subtítulo abaixo da manchete (ORD-F-149 item 1, proposta: a conta da definição matemática); ausente nos demais. */
  sub?: HeadlineLine;
  /** Chave de transição de H7: §7.4 anima só quando ela muda. */
  key: string;
}

export interface HeadlineTextOptions {
  /** `config.mode === 'simulation'`: variante `simulation` da contagem (§10.8). */
  simulation?: boolean;
  /** Líder guardado na memória (H2): vencedor de `decided` mantido por histerese sem decisão na view. */
  leaderNumber?: CandidateNumber | null;
  /** `config.election_date` (hora da virada depois da meia-noite, M15). */
  electionDate?: string;
  /**
   * Site ao vivo, sem dado e com o relógio da fonte ligado: antes da abertura (25/10, 17h00), a manchete diz quando a
   * apuração começa (a mesma contagem de §10.8: "no dia 25", "amanhã" ou o relógio), e não "Aguardando os resultados"
   * (ORD-F-054 item 2; desvio do AC-13.2-02, docs/DECISOES.md §54).
   * Nunca no replay (FB-02, ajuste 1: nenhum "começa" no replay).
   */
  preOpenHint?: boolean;
  /**
   * ORD-F-149 item 1 (em aprovação; a trava é `EDITORIAL[1]` de src/copy/editorial.ts, que `model/` não importa: quem
   * chama passa). 'today' (padrão): "{X} é eleito presidente" já na definição matemática. 'proposal': antes do TSE, "A
   * vantagem de {X} não pode mais ser alcançada" com o subtítulo da conta; depois do TSE, "…, segundo o TSE".
   * 'alternative': "{X} lidera por {V}" até o TSE.
   */
  decidedText?: 'today' | 'proposal' | 'alternative';
}

const line = (key: HeadlineCopyKey, vars: HeadlineVars = {}): HeadlineLine => ({ key, vars });

/** Variáveis de um candidato num marcador (`X`, `A`, `B`). */
function candVars(name: string, c: Candidate): Record<string, string | null> {
  return { [name]: c.display_name, [`${name}.number`]: c.number, [`${name}.gender`]: c.gender };
}

function candidate(view: ResultsView, num: CandidateNumber | null | undefined): Candidate | null {
  if (num === null || num === undefined) return null;
  return view.candidates.find((c) => c.number === num) ?? null;
}

function leadingText(view: ResultsView): HeadlineText {
  const x = candidate(view, leaderOf(view));
  if (x === null) {
    const [a, b] = view.candidates;
    return { line1: line('headline.tie', { ...candVars('A', a), ...candVars('B', b) }), line2: null, key: 'tie' };
  }
  const v = Math.abs(view.national.diffVotes);
  return {
    line1: line('headline.leading', candVars('X', x)),
    line2: { key: 'headline.leading.by', vars: { V: fmtVotesLong(v) }, animated: { name: 'V', value: v } },
    key: `leading:${x.number}`,
  };
}

/**
 * Pura. Linha 1, linha 2 opcional e a chave de transição (§13.2.1; H7), para o estado dado sobre `inp.view`.
 * Estados que precisam de dado ausente (histerese segurando um estado sem o evento ou a decisão) caem no texto de
 * `leading` (ou `tie`), que é o que os números mostram.
 */
/** O texto da contagem antes das 17h00 (§10.8): "no dia 25", "amanhã" ou o relógio regressivo do dia. */
function countdownText(clockTMin: number, opts: HeadlineTextOptions): HeadlineText {
  const variant = countdownVariant(clockTMin, opts.simulation === true);
  const line2 =
    variant === 'today' ? line('countdown.timer', { 'H:MM:SS': fmtCountdown((T_OPEN - clockTMin) * 60) }) : null;
  return { line1: line(`countdown.${variant}`), line2, key: `countdown:${variant}` };
}

export function headlineText(state: HeadlineState, inp: HeadlineInputs, opts: HeadlineTextOptions = {}): HeadlineText {
  const v = inp.view;
  if (state === 'connecting' || v === null)
    return opts.preOpenHint === true && inp.clockTMin < T_OPEN
      ? countdownText(inp.clockTMin, opts)
      : { line1: line('headline.connecting'), line2: null, key: 'connecting' };
  switch (state) {
    case 'countdown':
      return countdownText(inp.clockTMin, opts);
    case 'opened_no_votes':
      return { line1: line('headline.opened'), line2: null, key: 'opened' };
    case 'abroad_first':
      return { line1: line('headline.abroadFirst'), line2: null, key: 'abroad' };
    case 'early':
      return { line1: line('headline.early'), line2: null, key: 'early' };
    case 'above_99': {
      const x = candidate(v, leaderOf(v) ?? opts.leaderNumber);
      if (x === null) return leadingText(v);
      return { line1: line('headline.above99', candVars('X', x)), line2: null, key: `above99:${x.number}` };
    }
    case 'lead_change': {
      const lc = lastLeadChange(v);
      const x = candidate(v, lc?.data.to);
      if (lc === null || x === null) return leadingText(v);
      const date = opts.electionDate ?? currentElectionDate();
      const hhmm = fmtTime(tMinOfIso(lc.occurred_at, epochMs(date)), date);
      return {
        line1: line('headline.leadChange', { ...candVars('X', x), HHhMM: hhmm }),
        line2: null,
        key: `leadchange:${x.number}:${lc.seq}`,
      };
    }
    case 'decided':
    case 'elected_tse': {
      const n = v.national;
      const x = candidate(v, n.tseResult?.winner ?? n.mathDecision?.winner ?? opts.leaderNumber ?? leaderOf(v));
      if (x === null) return leadingText(v);
      const choice = opts.decidedText ?? 'today';
      if (choice !== 'today') return decidedText(state, v, x, choice);
      // H6: decided e elected_tse compartilham texto e chave (sem animação; só o chip muda, §7.3).
      return { line1: line('headline.elected', candVars('X', x)), line2: null, key: `final:${x.number}` };
    }
    default:
      return leadingText(v);
  }
}

/**
 * ORD-F-149 item 1 (em aprovação): o texto da definição na proposta e na alternativa. Proposta: antes do TSE, a
 * vantagem que não pode mais ser alcançada, com o subtítulo da conta ({S} das seções, {V} de diferença, {E} eleitores
 * por apurar; sem eleitores por apurar, sem subtítulo); com o TSE, "{X} é eleito presidente, segundo o TSE".
 * Alternativa: até o TSE, o texto de `leading` ("{X} lidera por {V}"); com o TSE, o de hoje.
 */
function decidedText(
  state: 'decided' | 'elected_tse',
  v: ResultsView,
  x: Candidate,
  choice: 'proposal' | 'alternative',
): HeadlineText {
  if (state === 'elected_tse') {
    return choice === 'proposal'
      ? { line1: line('headline.elected.tse', candVars('X', x)), line2: null, key: `tse:${x.number}` }
      : { line1: line('headline.elected', candVars('X', x)), line2: null, key: `final:${x.number}` };
  }
  if (choice === 'alternative') return leadingText(v);
  const n = v.national;
  const out: HeadlineText = {
    line1: line('headline.decided.lead', candVars('X', x)),
    line2: null,
    key: `decided:${x.number}`,
  };
  if (n.remainingElectorate > 0) {
    out.sub = line('headline.decided.lead.sub', {
      S: fmtSections(n.sectionsFraction),
      V: fmtInt(Math.abs(n.diffVotes)),
      E: fmtInt(n.remainingElectorate),
    });
  }
  return out;
}
